import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { FacebookIcon, List } from 'lucide-react';
import { connectFbPage, createFbLoginToken, fetchFbPages } from '@/api/fbapi';
import { FbLoginToken, FbPage, FBPost, FbRecentPost } from '@/types/fb-type';
import { ApiResponse } from '@/types/api-response';
import { FacebookPageCard } from '@/components/facebook/facebook-page';
import { useSearchParams } from "react-router-dom";
import { RecentPostCard } from '@/components/facebook/recent-post';
import { useToast } from '@/hooks/use-toast';
import { Redirect } from 'wouter';
import { navigate } from 'wouter/use-browser-location';
import { PostList } from '@/components/facebook/post-list';
import RedirectAlert from '@/components/settings/RedirectAlert';

function FbCallBackForm() {
    const { updateFacebookPage } = useAuth();
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [facebookPages, setFacebookPages] = useState<FbPage[]>([]);
    const [recentPosts, setRecentPosts] = useState<FBPost[]>([]);
    const [isConnectedPage, setIsConnectedPage] = useState<boolean>(false);
    const [connectedFacebookId, setConnectedFacebookId] = useState<string | null>(null);
    const [showRedirect, setShowRedirect] = useState<boolean>(false);

    const { toast } = useToast();
    useEffect(() => {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");
        const state = url.searchParams.get("state");

        if (!code || !state) return; // guard

        const loadPage = async () => {
            try {
                setIsLoading(true);
                const fbListPageResponse = await fetchFbPages(code, state);
                if (fbListPageResponse?.success) {
                    setFacebookPages(fbListPageResponse.data);
                } else {
                    console.error("error fb page response", fbListPageResponse);
                    setError(fbListPageResponse.message ?? "Unexpected error");
                }
            } catch (err: any) {
                console.error(err);
                setError(err.message ?? "Unexpected error");
            } finally {
                setIsLoading(false);
            }
        };

        loadPage();
    }, [window.location.href]); // only rerun if query params change


    const onSubmit = async (e: any) => {
        try {
            e.preventDefault();
            setIsLoading(true);
            setError(null);


        } catch (err: any) {


            // Handle different types of errors
            if (err.response?.status === 401) {
                setError('Invalid username or password');
            } else if (err.response?.status === 400) {
                setError(err.response.data?.message || 'Invalid request data');
            } else if (err.code === 'NETWORK_ERROR') {
                setError('Network error. Please check your connection.');
            } else if (err.code === 'ECONNABORTED') {
                setError('Request timeout. Please try again.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onConnect = async (pageId: string) => {
        const connectResult = await connectFbPage(pageId);
        if (connectResult?.success) {
            setIsConnectedPage(true);
            setConnectedFacebookId(pageId);
            setRecentPosts(connectResult.data);
            const connectedPage = facebookPages.find((p) => String(p.pageId) === String(pageId));
            if (connectedPage) updateFacebookPage(connectedPage);
            toast({ title: "Connected", description: "Your Facebook Page was connected successfully!" });
            // setTimeout(() => {
            //     navigate("/"); // redirect to dashboard
            // }, 3000);
            setShowRedirect(true);
        }
        else {
            setError(connectResult?.message!);
        }
    };

    return (
        <>
            <div className=" bg-gray-50 flex flex-col justify-center py-6 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Your Facebook Pages
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Login with Facebook
                    </p>
                </div>
                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white py-8 px-4 shadow sm:rounded-lg ">
                        {error && (
                            <Alert variant="destructive" className="mb-4">
                                <AlertTitle>Integrate With Facebook Error</AlertTitle>
                                <AlertDescription>
                                    <div className="space-y-2">
                                        <div>{error}</div>

                                        {/* Try Again Link */}
                                        <a
                                            href="/fblogin"
                                            className="text-sm font-medium text-red-900 underline hover:text-red-700"
                                        >
                                            Try Again → Facebook Login
                                        </a>
                                    </div>
                                </AlertDescription>
                            </Alert>
                        )}
                        <form className="space-y-6" onSubmit={onSubmit}>
                            <div className="flex items-center gap-2 text-indigo-700 font-semibold mb-3">
                                <FacebookIcon className="h-5 w-5" />

                                {isLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent"></div>
                                        <span>Fetching Facebook Pages...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Your Managed Facebook Pages</span>
                                    </>
                                )}
                            </div>

                            <div className="space-y-4">
                                {isLoading ? (
                                    // Skeleton loading for cards
                                    <>
                                        <div className="animate-pulse h-24 bg-gray-100 rounded-xl"></div>
                                        <div className="animate-pulse h-24 bg-gray-100 rounded-xl"></div>
                                    </>
                                ) : (
                                    facebookPages.map((facebookPage: FbPage) => (
                                        <div key={facebookPage.pageId}>
                                            <FacebookPageCard
                                                facebeookPage={facebookPage}
                                                onConnect={onConnect}
                                            />
                                        </div>
                                    ))
                                )}
                            </div>

                        </form>

                    </div>
                </div>
            </div>
            {showRedirect && <RedirectAlert
                to="/"
                reload
                seconds={5}
                message="Login successful! Redirecting to dashboard..."
            />
            }
            {isConnectedPage && <>
                <div className="w-full flex justify-center py-10">
                    <div className="w-full md:w-1/2 lg:w-1/2">
                        <div className="border rounded-xl shadow-sm p-6 bg-white">

                            <PostList posts={recentPosts} />
                        </div>
                    </div>
                </div>
            </>}
        </>
    );
}

export default function FbCallBackPage() {
    const { isAuthenticated, isLoading: authLoading } = useAuth();

    // Show loading state while checking auth status
    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not yet login
    if (!isAuthenticated) {
        return <Redirect to="/login" replace />;
    }

    return <FbCallBackForm />;
}
